import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X, FloppyDisk, ArrowsClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useSector } from "@/lib/sectorContext";

/**
 * DailyBoardTab — interactive agenda for the day. Operator paints
 * slots as available / blocked / reserved-emergency, defines
 * technicians and lunch blocks, and can replicate the model forward
 * for 7 or 30 days.
 *
 * v0.99.50 · Fev/2026 — sector-aware labels. "Técnicos" só faz sentido
 * em oficina; outros setores veem "Equipe" (termo neutro). Clínica tem
 * especialização em "Profissionais", Pet Shop/Salão/Academia usam
 * "Equipe", Igreja/Mandato não tem conceito de staff por slot (card
 * segue oculto? — mantemos "Equipe do dia" como opção pro operador
 * marcar atendentes voluntários/assessores).
 */
function useTeamLabel() {
  const { isAutomotive, isClinic } = useSector();
  if (isAutomotive) return { singular: "Técnico", plural: "Técnicos" };
  if (isClinic) return { singular: "Profissional", plural: "Profissionais" };
  return { singular: "Pessoa da equipe", plural: "Equipe" };
}

const DEFAULT_HOURS = Array.from({ length: 12 }, (_, i) => {
  const h = (7 + i).toString().padStart(2, "0");
  return `${h}:00`;
});

const STATUS_OPTIONS = [
  { value: "available", label: "Disponível", dot: "bg-slate-200" },
  { value: "full", label: "Cheio", dot: "bg-slate-900" },
  { value: "blocked", label: "Bloqueado", dot: "bg-slate-400" },
  { value: "reserved_emergency", label: "Encaixe", dot: "bg-amber-500" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "short" });
  } catch { return iso; }
}

function isoShift(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function DailyBoardTab({ onChange, onToast }) {
  const team = useTeamLabel();
  const [date, setDate] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [capacity, setCapacity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replicating, setReplicating] = useState(false);
  const [tnInput, setTnInput] = useState("");

  const load = async (iso) => {
    setLoading(true);
    try {
      const res = await api.get(`/operacao/board/${iso}`);
      if (res.data?.configured === false) {
        // bootstrap a default skeleton
        setSlots(DEFAULT_HOURS.map((t) => ({
          time: t, capacity: 2, available_technicians: 2, status: "available",
          appointments: [], reserved_for: null,
        })));
        setTechnicians([]);
        setCapacity(0);
      } else {
        setSlots(res.data.slots || []);
        setTechnicians(res.data.technicians || []);
        setCapacity(res.data.daily_capacity || 0);
      }
    } catch (e) {
      onToast?.("Erro ao carregar agenda.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); }, [date]);

  const cycleStatus = (idx) => {
    setSlots((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const order = STATUS_OPTIONS.map((o) => o.value);
      const next = order[(order.indexOf(s.status) + 1) % order.length];
      return { ...s, status: next };
    }));
  };

  const updateSlot = (idx, patch) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addSlot = () => {
    setSlots((prev) => [...prev, {
      time: "08:00", capacity: 2, available_technicians: 2,
      status: "available", appointments: [], reserved_for: null,
    }]);
  };

  const removeSlot = (idx) => {
    setSlots((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTechnician = () => {
    const n = tnInput.trim();
    if (!n) return;
    setTechnicians([...technicians, { id: Math.random().toString(36).slice(2), name: n, specialty: [], shift: "" }]);
    setTnInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/operacao/board/${date}`, {
        date,
        slots: slots.map((s) => ({
          time: s.time,
          capacity: Number(s.capacity) || 1,
          available_technicians: Number(s.available_technicians) || 1,
          status: s.status,
          appointments: s.appointments || [],
          reserved_for: s.reserved_for || null,
          reason: s.reason || null,
        })),
        technicians,
        daily_capacity: Number(capacity) || 0,
        blocks: [],
      });
      onToast?.("Agenda salva.");
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const replicate = async (days, skipWeekends = false) => {
    setReplicating(true);
    try {
      const res = await api.post(
        `/api/operacao/board/${date}/replicate?days=${days}&skip_weekends=${skipWeekends}`,
      );
      onToast?.(
        `${res.data.created_count} dia(s) replicado(s)${res.data.skipped_count ? ` · ${res.data.skipped_count} pulado(s)` : ""}.`,
      );
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao replicar.", "error");
    } finally {
      setReplicating(false);
    }
  };

  const sorted = useMemo(
    () => [...slots].sort((a, b) => (a.time || "").localeCompare(b.time || "")),
    [slots]
  );

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="board-tab">
      {/* Date nav */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDate(isoShift(date, -1))}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            data-testid="board-prev"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-1.5 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
            data-testid="board-date"
          />
          <button
            type="button"
            onClick={() => setDate(isoShift(date, 1))}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            data-testid="board-next"
          >
            <CaretRight size={14} weight="bold" />
          </button>
          <span className="text-[12px] text-slate-500 ml-2 capitalize">{fmtDate(date)}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => replicate(7, false)}
            disabled={replicating}
            data-testid="board-replicate-7"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-[12px] text-slate-700"
          >
            <ArrowsClockwise size={11} weight="bold" />
            Replicar 7 dias
          </button>
          <button
            type="button"
            onClick={() => replicate(30, true)}
            disabled={replicating}
            data-testid="board-replicate-30"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-[12px] text-slate-700"
          >
            <ArrowsClockwise size={11} weight="bold" />
            Replicar 30 dias úteis
          </button>
        </div>
      </div>

      {/* Technicians + capacity */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-100 p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">{team.plural} do dia</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {technicians.length === 0 && (
              <span className="text-[12px] text-slate-400">Nenhum.</span>
            )}
            {technicians.map((t, i) => (
              <span key={t.id || i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900 text-white text-[12px]">
                {t.name}
                <button
                  type="button"
                  onClick={() => setTechnicians(technicians.filter((_, idx) => idx !== i))}
                  className="hover:opacity-70"
                >
                  <X size={10} weight="bold" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={tnInput}
              onChange={(e) => setTnInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTechnician(); } }}
              placeholder="Ex.: Carlos"
              className="flex-1 px-3 py-1.5 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
              data-testid="board-tech-input"
            />
            <button
              type="button"
              onClick={addTechnician}
              data-testid="board-tech-add"
              className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-[12px]"
            >
              <Plus size={11} weight="bold" />
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Capacidade total do dia</div>
          <input
            type="number"
            min="0"
            value={capacity || ""}
            onChange={(e) => setCapacity(parseInt(e.target.value, 10) || 0)}
            placeholder="Ex.: 16"
            className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
            data-testid="board-capacity"
          />
          <p className="text-[11px] text-slate-400 mt-2">
            Atendimentos simultâneos máximos. A IA respeita esse limite ao sugerir horários.
          </p>
        </div>
      </div>

      {/* Slots grid */}
      <div className="rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Slots do dia</h3>
          <button
            type="button"
            onClick={addSlot}
            data-testid="board-add-slot"
            className="text-[12px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <Plus size={11} weight="bold" /> Adicionar slot
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {sorted.map((s, idx) => {
            const realIdx = slots.indexOf(s);
            const meta = STATUS_OPTIONS.find((o) => o.value === s.status) || STATUS_OPTIONS[0];
            return (
              <div
                key={`${s.time}-${realIdx}`}
                data-testid={`board-slot-${s.time}`}
                className="px-5 py-3 grid grid-cols-[80px_1fr_120px_120px_auto] gap-3 items-center hover:bg-slate-50"
              >
                <input
                  type="time"
                  value={s.time}
                  onChange={(e) => updateSlot(realIdx, { time: e.target.value })}
                  className="px-2 py-1 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => cycleStatus(realIdx)}
                  className="inline-flex items-center gap-2 text-[13px] text-slate-700 hover:text-slate-900 group"
                >
                  <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                  <span>{meta.label}</span>
                </button>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">Capacidade</label>
                  <input
                    type="number"
                    min="0"
                    value={s.capacity ?? 1}
                    onChange={(e) => updateSlot(realIdx, { capacity: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-2 py-0.5 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none tabular-nums"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">{team.plural}</label>
                  <input
                    type="number"
                    min="0"
                    value={s.available_technicians ?? 1}
                    onChange={(e) => updateSlot(realIdx, { available_technicians: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-2 py-0.5 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none tabular-nums"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeSlot(realIdx)}
                  className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            );
          })}
          {slots.length === 0 && (
            <div className="px-5 py-10 text-center text-[12px] text-slate-400">
              Nenhum slot. Adicione um para começar.
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="board-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium disabled:opacity-50"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Salvando…" : "Salvar agenda"}
        </button>
      </div>
    </div>
  );
}
